/**
 * The league chip row, owned in one place. Mounted once by the Scores route
 * above both game panes, so the row is the same DOM node on the `All` feed
 * and on a league view and never unmounts when `?leagueId=` changes
 * (ISSUE-949).
 */
import { useProfileGet } from "@sportsmet/profile-sdk/private";
import {
  useLeagueCatalogGet,
  useTeamGetMany,
} from "@sportsmet/sports-sdk/private";
import { type ReactNode } from "react";

import { LeagueSelectorRow } from "./LeagueSelectorRow";
import { buildLeagueSelectorRow } from "./leagueSelectorRowModel";

export type typeScoresLeagueTabsProps = {
  /** The league being viewed, or null on the `All` feed. Marks that chip. */
  activeLeagueId: string | null;
  onSelectAll: () => void;
  onSelectLeague: (leagueId: string) => void;
  onOpenMenu: () => void;
};

export const ScoresLeagueTabs = (
  props: typeScoresLeagueTabsProps,
): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const profile = useProfileGet({});
  const sports = catalog.data?.data?.sports ?? [];
  const pinnedLeagueIds = profile.data?.data?.profile?.followedLeagueIds ?? [];
  const favoriteTeamIds = profile.data?.data?.profile?.favoriteTeamIds ?? [];

  // Turns the fan's favorite team ids into league ids so a favorited team's
  // league is a pill even with no pin. Gated, so a fan with no favorites makes
  // no extra call, and deliberately outside any loading gate: the pins render
  // at once and the favorite pills join when this resolves.
  const favoriteTeams = useTeamGetMany(
    { teamIds: favoriteTeamIds },
    { enabled: favoriteTeamIds.length > 0 },
  );
  const favoriteLeagueIds = (favoriteTeams.data?.data?.teams ?? []).map(
    (team) => team.leagueId,
  );

  const row = buildLeagueSelectorRow({
    sports,
    pinnedLeagueIds,
    favoriteLeagueIds,
    activeLeagueId: props.activeLeagueId,
  });

  // No wrapper element on purpose: the row stays a direct flex child of
  // `SectionScreen`'s `flex flex-col gap-4`, so its spacing is unchanged.
  return (
    <LeagueSelectorRow
      row={row}
      onSelectAll={props.onSelectAll}
      onSelectLeague={props.onSelectLeague}
      onOpenMenu={props.onOpenMenu}
    />
  );
};
